import { createReducer, on } from '@ngrx/store';
import _ from 'lodash';
import { OrderFilterKey, OrderSortKey } from '../../../enums';
import { GuestData, Order } from '../../../models';
import {
  addOrder,
  removeOrder,
  updateOrder,
  setOrders,
  newMessageForOrder,
  sortOrders,
  filterOrders,
} from './open-order.actions';

export const openOrdersInitialState: {
  sort: OrderSortKey;
  filter: Partial<Record<OrderFilterKey, string>>;
  data: Order[];
  filteredData: Order[];
  lastUpdated: Date;
} = {
  sort: OrderSortKey.ORDER_NUMBER,
  filter: null,
  data: [],
  filteredData: [],
  lastUpdated: null,
};

function applySort(orders: Order[], sort: OrderSortKey): Order[] {
  return [...orders].sort((a, b) => {
    if (sort === 'orderNumber') {
      return b.data.orderNumber - a.data.orderNumber;
    }
    if (sort === 'name') {
      const tableA = _.lowerCase(a.getTableName() || '');
      const tableB = _.lowerCase(b.getTableName() || '');
      const guestAName = _.lowerCase((a.data.guest as GuestData)?.name);
      const guestBName = _.lowerCase((b.data.guest as GuestData)?.name);
      if (tableA && tableB) {
        return tableA.localeCompare(tableB);
      }
      if (tableA && !tableB) {
        return tableA.localeCompare(guestBName);
      }
      if (!tableA && tableB) {
        return guestAName.localeCompare(tableB);
      }
      return guestAName.localeCompare(guestBName);
    }
    return a.id.localeCompare(b.id);
  });
}

function getDiff(newOrders: Order[], stateOrders: Order[]): Order[] {
  const serverIds = new Set(newOrders.map(order => order.id));
  const seen = new Set<string>();
  const kept = stateOrders.filter(order => {
    if (!serverIds.has(order.id) || seen.has(order.id)) {
      return false;
    }
    seen.add(order.id);
    return true;
  });
  newOrders.forEach(newOrder => {
    if (!seen.has(newOrder.id)) {
      kept.push(newOrder);
      seen.add(newOrder.id);
    }
  });
  return kept;
}

function applyFilter(orders: Order[], filter: Partial<Record<OrderFilterKey, string>>): Order[] {
  if (!filter) {
    return orders;
  }
  let filtered = orders;

  // Shift (server/employee)
  if (filter[OrderFilterKey.SHIFT_ID]) {
    filtered = filtered.filter(order => {
      return order.data.items.find(item => item.shift === filter[OrderFilterKey.SHIFT_ID]);
    });
  }

  // Guest name search
  if (filter[OrderFilterKey.GUEST_NAME]) {
    const search = filter[OrderFilterKey.GUEST_NAME].toLowerCase();
    filtered = filtered.filter(order => {
      const guest = order.data.guest as GuestData;
      return guest?.name?.toLowerCase().includes(search);
    });
  }

  // Payment status: paid | unpaid | missing-payment
  if (filter[OrderFilterKey.PAYMENT_STATUS]) {
    const status = filter[OrderFilterKey.PAYMENT_STATUS];
    filtered = filtered.filter(order => {
      if (status === 'paid') {return !!order.data.paid;}
      if (status === 'unpaid') {return !order.data.paid;}
      if (status === 'missing-payment') {return order.isMissingPayment;}
      return true;
    });
  }

  // Order type: TABLE, TAB, EVENT, etc. (matches DiningOptionsBehavior)
  if (filter[OrderFilterKey.ORDER_TYPE]) {
    const type = filter[OrderFilterKey.ORDER_TYPE];
    filtered = filtered.filter(order => {
      return order.data.orderDestination?.behaviour === type;
    });
  }

  // Time range: today | last24h | all
  if (filter[OrderFilterKey.TIME_RANGE] && filter[OrderFilterKey.TIME_RANGE] !== 'all') {
    const now = Date.now();
    filtered = filtered.filter(order => {
      const created = new Date(order.data.created).getTime();
      if (filter[OrderFilterKey.TIME_RANGE] === 'today') {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        return created >= startOfDay.getTime();
      }
      if (filter[OrderFilterKey.TIME_RANGE] === 'last24h') {
        return now - created <= 86400000;
      }
      return true;
    });
  }

  // Card on file: 'true' = has card on file, 'false' = no card on file
  if (filter[OrderFilterKey.CARD_ON_FILE]) {
    const hasCard = filter[OrderFilterKey.CARD_ON_FILE] === 'true';
    filtered = filtered.filter(order => {
      const orderHasCard = !!order.data.cardOnFile || !!order.data.guestCardsOnFile?.length;
      return hasCard ? orderHasCard : !orderHasCard;
    });
  }

  return filtered;
}

export const openOrdersReducer = createReducer(
  openOrdersInitialState,
  on(setOrders, (state, { orders }) => {
    const data = applySort(getDiff(orders, state.data), state.sort);
    return {
      ...state,
      data,
      filteredData: applyFilter(data, state.filter),
      lastUpdated: new Date(),
    };
  }),
  on(addOrder, (state, { order }) => {
    if (state.data.some(existing => existing.id === order.id)) {
      return state;
    }
    const data = applySort([...state.data, order], state.sort);
    return {
      ...state,
      data,
      filteredData: applyFilter(data, state.filter),
      lastUpdated: new Date(),
    };
  }),
  on(removeOrder, (state, { orderId }) => {
    const data = state.data.filter(o => o.id !== orderId);
    return {
      ...state,
      data,
      filteredData: applyFilter(data, state.filter),
      lastUpdated: new Date(),
    };
  }),
  on(updateOrder, (state, { orderId, update }) => {
    if (!state.data.some(order => order.id === orderId)) {
      return state;
    }
    const data = state.data.map(order => {
      if (order.id !== orderId) {
        return order;
      }
      const cloned = new Order(order.json);
      cloned.extend(update);
      return cloned;
    });
    return {
      ...state,
      data,
      filteredData: applyFilter(data, state.filter),
      lastUpdated: new Date(),
    };
  }),
  on(sortOrders, (state, { sort }) => {
    const data = applySort([...state.data], sort);
    return {
      ...state,
      data,
      filteredData: applyFilter(data, state.filter),
    };
  }),
  on(filterOrders, (state, { filter }) => {
    return {
      ...state,
      filter: filter ?? state.filter,
      filteredData: applyFilter(state.data, filter),
    };
  }),
  on(newMessageForOrder, (state, { orderId }) => {
    const order = state.data.find(openOrder => {
      if (openOrder.id === orderId) {
        return true;
      }
      return false;
    });
    if (order) {
      if (!order.messages) {
        order.messages = {
          _id: order.id,
          count: 0,
          unread: 0,
        };
      }
      order.messages.count++;
      order.messages.unread++;
      return state;
    }
  })
);
